import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Chip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Checkbox,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  ListItemText,
  CircularProgress,
  Snackbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import axios from 'axios';
import { belarusRegions, regionsList, getCitiesByRegion, getRegionCenter } from '../../data/belarusRegions';
import LivePreview from '../../components/LivePreview';
import {
  normalizeBuilderSelectionLimits,
  countBuilderOptions,
  selectionCardSuffix,
} from '../../utils/multiSelectLimit';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const stickyPreviewSx = {
  position: { xs: 'static', md: 'sticky' },
  top: { md: 16 },
  maxHeight: { xs: 'none', md: 'calc(100vh - 32px)' },
  overflow: { md: 'auto' },
};

const pageHeaderRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', sm: 'center' },
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 1,
  mb: 2,
};

const stepActionsSx = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  justifyContent: 'space-between',
  alignItems: { xs: 'stretch', sm: 'center' },
  gap: 2,
  mt: 4,
};

const stepActionsGroupSx = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 1,
  width: { xs: '100%', sm: 'auto' },
  '& .MuiButton-root': {
    width: { xs: '100%', sm: 'auto' },
  },
};

const movePageFormSx = {
  minWidth: { xs: '100%', sm: 120 },
  width: { xs: '100%', sm: 'auto' },
};

const questionActionsRowSx = {
  display: 'flex',
  gap: 1,
  flexWrap: 'wrap',
};

const regularSteps = ['Основная информация', 'Вопросы', 'Логика и ветвления', 'Квоты'];
const profileSurveySteps = ['Основная информация', 'Вопросы', 'Логика и ветвления'];
const publicLinkSteps = ['Основная информация', 'Вопросы', 'Логика и ветвления', 'Настройки'];
const externalSurveySteps = ['Основная информация', 'Квоты'];

const questionTypes = [
  { value: 'single', label: 'Один из списка' },
  { value: 'multiple', label: 'Несколько из списка' },
  { value: 'single-image', label: 'Один из списка (с картинками)' },
  { value: 'multiple-image', label: 'Несколько из списка (с картинками)' },
  { value: 'dropdown', label: 'Выпадающий список' },
  { value: 'ranking', label: 'Ранжирование вариантов' },
  { value: 'matrix', label: 'Матрица/таблица оценок' },
  { value: 'likert', label: 'Согласие/несогласие по утверждению' },
  { value: 'text', label: 'Текстовый ответ' },
  { value: 'scale', label: 'Шкала (с задаваемыми значениями)' },
  { value: 'file', label: 'Загрузка файла' }
];

const profileFields = [
  { value: 'gender', label: 'Пол', options: ['Мужчина', 'Женщина', 'Другое'] },
  { value: 'ageRange', label: 'Возраст', type: 'range' },
  { value: 'city', label: 'Город', type: 'text' },
  { value: 'region', label: 'Регион', type: 'text' },
  { value: 'education', label: 'Образование', options: ['Среднее', 'Высшее', 'Послевузовское'] },
  { value: 'maritalStatus', label: 'Семейное положение', options: ['Не женат/Не замужем', 'Женат/Замужем', 'Разведен(а)', 'Вдовец/Вдова'] },
  { value: 'hasChildren', label: 'Есть дети', options: ['Да', 'Нет'] },
  { value: 'hasCar', label: 'Владеет машиной', options: ['Да', 'Нет'] },
  { value: 'employment', label: 'Занятость', options: ['Работаю', 'Безработный', 'Студент', 'Пенсионер', 'Самозанятый'] },
  { value: 'income', label: 'Доход', type: 'range' }
];

export default function SurveyBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id: surveyId } = useParams(); // Для редактирования
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // Shown once after "Дублировать" on the dashboard opened this copy
  const [duplicateNotice, setDuplicateNotice] = useState(
    () => Boolean(surveyId && location.state && location.state.duplicatedFrom)
  );

  // Step 1: Основная информация
  const [basicInfo, setBasicInfo] = useState({
    title: '',
    description: '',
    category: 'online-surveys',
    estimatedTime: 5,
    allowMultipleCompletions: false
  });

  // Step 2: Целевая аудитория
  const [targetAudience, setTargetAudience] = useState({
    gender: [],
    ageRange: { min: 18, max: 65 },
    regions: [],
    cities: [],
    education: [],
    income: { min: null, max: null }
  });

  // Step 3: Вопросы
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);

  // NEW: Pages structure
  const [pages, setPages] = useState([
    { id: 'page-1', title: 'Страница 1', questionIds: [] }
  ]);
  const [currentPageId, setCurrentPageId] = useState('page-1');

  // Step 4: Логика и ветвления (теперь между страницами)
  const [logic, setLogic] = useState([]); // Старая логика (deprecated)
  const [pageLogic, setPageLogic] = useState([]); // Новая логика переходов между страницами

  // Step 5: Квоты (динамические условия по профильным вопросам)
  const [quotas, setQuotas] = useState([]); // { id, description, limit, filled, criteria: { dynamic: [{ id, questionId, answers: [] }] } }
  const [profileQuestions, setProfileQuestions] = useState([]); // активные профильные вопросы для конструктора квот
  const [loadingProfileQuestions, setLoadingProfileQuestions] = useState(false);

  // Step 6: Вознаграждение (админ может назначать и деньги, и баллы)
  const [reward, setReward] = useState({
    moneyAmount: 0,
    pointsAmount: 0,
    currency: 'BYN'
  });
  const [maxResponses, setMaxResponses] = useState(100);
  // Админ: сопоставление вопросов опроса с вопросами профиля (квоты для ответов интервьюеров)
  const [interviewerQuotaMapping, setInterviewerQuotaMapping] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  // Лимит ответов: сумма квот для новых опросов и для заказчика;
  // при редактировании существующего опроса админом — значение, заданное админом (не перезаписывается суммой квот).
  const quotasLimitSum = () => quotas.reduce((sum, q) => sum + (Number(q.limit) || 0), 0);
  const isAdminEditingExisting = () => isAdmin && Boolean(surveyId) && Boolean(loadedSurvey?.maxResponses);
  // --- Сопоставление для квот интервьюеров (только админ) ---
  const choiceQuestions = () => (questions || []).filter(q => ['single', 'multiple', 'dropdown'].includes(q.type) && Array.isArray(q.options) && q.options.length > 0);
  const updateMappingRule = (ruleId, patch) => setInterviewerQuotaMapping(prev => prev.map(m => (m.id === ruleId ? { ...m, ...patch } : m)));
  const addMappingRule = () => setInterviewerQuotaMapping(prev => [...prev, { id: `qm_${Date.now()}`, surveyQuestionId: '', profileQuestionId: '', values: {} }]);
  const removeMappingRule = (ruleId) => setInterviewerQuotaMapping(prev => prev.filter(m => m.id !== ruleId));
  const renderInterviewerQuotaMapping = () => (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>Квоты для ответов интервьюеров (админ)</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        У ответов интервьюеров нет профиля. Сопоставьте вопросы этого опроса с вопросами профиля, на которых построены квоты.
        Если правил нет, ответы интервьюеров квотами не проверяются (как раньше). Если правила есть, ответ, не подходящий ни под одну квоту
        или попадающий в заполненную квоту, будет отклонён. Вариант без сопоставления не даёт значения.
      </Alert>
      {interviewerQuotaMapping.map(rule => {
        const sq = choiceQuestions().find(q => q.id === rule.surveyQuestionId);
        const pq = profileQuestions.find(q => q.questionId === rule.profileQuestionId);
        return (
          <Paper key={rule.id} variant="outlined" sx={{ p: 2, mb: 1, bgcolor: '#fafafa' }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Вопрос опроса</InputLabel>
                  <Select value={rule.surveyQuestionId || ''} label="Вопрос опроса"
                    onChange={(e) => updateMappingRule(rule.id, { surveyQuestionId: e.target.value, values: {} })}>
                    {choiceQuestions().map(q => (
                      <MenuItem key={q.id} value={q.id}>{String(q.text || '').substring(0, 70)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Вопрос профиля</InputLabel>
                  <Select value={rule.profileQuestionId || ''} label="Вопрос профиля"
                    onChange={(e) => updateMappingRule(rule.id, { profileQuestionId: e.target.value, values: {} })}>
                    {profileQuestions.map(p => (
                      <MenuItem key={p.questionId} value={p.questionId}>{p.surveyTitle}: {String(p.questionText || '').substring(0, 60)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <IconButton color="error" size="small" onClick={() => removeMappingRule(rule.id)}><DeleteIcon /></IconButton>
              </Grid>
              {sq && pq && sq.options.map(opt => (
                <Grid item xs={12} key={opt}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={5}><Typography variant="body2">{opt}</Typography></Grid>
                    <Grid item xs={12} md={7}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Ответы профиля</InputLabel>
                        <Select multiple label="Ответы профиля"
                          value={(rule.values && rule.values[opt]) || []}
                          renderValue={(sel) => (sel || []).join(', ')}
                          onChange={(e) => updateMappingRule(rule.id, { values: { ...(rule.values || {}), [opt]: e.target.value } })}>
                          {pq.options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Paper>
        );
      })}
      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addMappingRule} disabled={profileQuestions.length === 0}>
        Добавить сопоставление
      </Button>
    </Paper>
  );

  const resolveMaxResponses = () => {
    if (isAdminEditingExisting() && Number(maxResponses) > 0) return Number(maxResponses);
    return quotasLimitSum();
  };
  // Pagination: number of questions per page for respondents (moved from respondent profile setting)
  const [questionsPerPage, setQuestionsPerPage] = useState(1);
  // Храним загруженный опрос для доступа к его статусу
  const [loadedSurvey, setLoadedSurvey] = useState(null);
  
  // Profile survey fields
  const [isProfileSurvey, setIsProfileSurvey] = useState(false);
  
  // Public link survey
  const [isPublicLink, setIsPublicLink] = useState(false);
  const [customSlug, setCustomSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [profileCategory, setProfileCategory] = useState('');
  const [questionMapping, setQuestionMapping] = useState([]);

  // External survey (redirect to external URL)
  const [isExternalSurvey, setIsExternalSurvey] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');

  // Bitrix24 integration (step shown only if client webhook is configured)
  const defaultBitrixConfig = {
    enabled: false,
    entityType: 'lead',
    categoryId: null,
    stageId: null,
    statusId: null,
    assignmentMode: 'single',
    managerIds: [],
    titleTemplate: 'Заявка из опроса ThinkLink {fullName}',
    contactMapping: {
      fullNameQuestionId: null,
      phoneQuestionId: null,
      emailQuestionId: null
    },
    includeAnswersInComments: true
  };
  const [bitrixAvailable, setBitrixAvailable] = useState(false);
  const [bitrixConfig, setBitrixConfig] = useState(defaultBitrixConfig);
  const [bitrixManagers, setBitrixManagers] = useState([]);
  const [bitrixCategories, setBitrixCategories] = useState([]);
  const [bitrixMetaLoading, setBitrixMetaLoading] = useState(false);


  // Загрузка данных опроса при редактировании
  useEffect(() => {
    console.log('useEffect triggered, surveyId:', surveyId);
    if (surveyId) {
      console.log('Calling loadSurveyData with:', surveyId);
      loadSurveyData(surveyId);
    } else {
      // Check if we're creating a profile survey from query param
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('profileSurvey') === 'true') {
        setIsProfileSurvey(true);
      }
    }
  }, [surveyId, location.search]);

  const loadSurveyData = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const survey = response.data;
      console.log('Loading survey for edit:', survey);
      console.log('Survey status:', survey.status);
      
      // Сохраняем загруженный опрос в state для дальнейшего использования
      setLoadedSurvey(survey);
      
      // Проверка: можно редактировать только черновики, отклоненные опросы и опросы на модерации (для админа)
      // Профильные опросы могут редактироваться администратором в любом статусе
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const isAdmin = user.role === 'admin';
      console.log('User role:', user.role, 'isAdmin:', isAdmin);
      
      // Админ может редактировать опрос в любом статусе (в т.ч. активный и завершённый)
      const canEdit = isAdmin ||
                      survey.status === 'draft' || 
                      survey.status === 'rejected';
      console.log('Can edit:', canEdit);
      
      if (!canEdit) {
        setError('Редактирование возможно только для черновиков и отклоненных опросов');
        setLoading(false);
        return;
      }
      
      // Загружаем все данные опроса
      console.log('Loading survey data into form...');
      setBasicInfo({
        title: survey.title || '',
        description: survey.description || '',
        category: survey.category || '',
        estimatedTime: survey.estimatedTime || 10,
        allowMultipleCompletions: survey.allowMultipleCompletions || false
      });
      console.log('Basic info loaded:', survey.title);
      
      if (survey.targetAudience) {
        console.log('Loading target audience:', survey.targetAudience);
        setTargetAudience({
          gender: survey.targetAudience.gender || [],
          ageRange: survey.targetAudience.ageRange || { min: 18, max: 65 },
          regions: survey.targetAudience.regions || [],
          cities: survey.targetAudience.cities || [],
          education: survey.targetAudience.education || [],
          income: survey.targetAudience.income || { min: null, max: null }
        });
      }
      
      if (survey.questions && Array.isArray(survey.questions)) {
        console.log('Loading questions:', survey.questions.length);
        setQuestions(survey.questions);
      }
      
      if (survey.logic && Array.isArray(survey.logic)) {
        console.log('Loading logic:', survey.logic.length);
        setLogic(survey.logic);
      }
      
      // Load pages structure
      if (survey.pages && Array.isArray(survey.pages) && survey.pages.length > 0) {
        console.log('Loading pages:', survey.pages.length);
        setPages(survey.pages);
        setCurrentPageId(survey.pages[0]?.id || 'page-1');
      } else {
        // Create default page with all questions
        console.log('No pages found, creating default page');
        const defaultPage = {
          id: 'page-1',
          title: 'Страница 1',
          questionIds: (survey.questions || []).map(q => q.id)
        };
        setPages([defaultPage]);
        setCurrentPageId('page-1');
      }
      
      // Load page logic
      if (survey.pageLogic && Array.isArray(survey.pageLogic)) {
        console.log('Loading pageLogic:', survey.pageLogic.length, survey.pageLogic);
        setPageLogic(survey.pageLogic);
      } else {
        console.log('No pageLogic found in survey');
      }
      
      if (survey.quotas && Array.isArray(survey.quotas)) {
        console.log('Loading quotas:', survey.quotas.length);
        const normalized = survey.quotas.map(q => {
          const crit = q.criteria || {};
          const dyn = Array.isArray(crit.dynamic) ? crit.dynamic : [];
          const dynWithIds = dyn.map(d => ({
            // ensure stable unique id for React keys and controlled inputs isolation
            id: d.id || `cond_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
            questionId: d.questionId || '',
            answers: Array.isArray(d.answers) ? d.answers : []
          }));
          return {
            id: q.id || `quota_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
            description: q.description || '',
            limit: Number.isFinite(Number(q.limit)) ? Number(q.limit) : 1,
            filled: Number(q.filled) || 0,
            criteria: { ...crit, dynamic: dynWithIds }
          };
        });
        setQuotas(normalized);
      }
      
      if (survey.reward) {
        console.log('Loading reward:', survey.reward);
        const r = survey.reward || {};
        const mapped = {
          moneyAmount: typeof r.moneyAmount === 'number' ? r.moneyAmount
                      : (r.type === 'money' ? (r.amount || 0) : (r.money?.amount || 0)),
          pointsAmount: typeof r.pointsAmount === 'number' ? r.pointsAmount
                       : (r.type === 'points' ? (r.amount || 0) : (r.points?.amount || 0)),
          currency: r.currency || 'BYN'
        };
        setReward(mapped);
      }
      
      if (Array.isArray(survey.interviewerQuotaMapping)) {
        setInterviewerQuotaMapping(survey.interviewerQuotaMapping);
      }

      if (survey.maxResponses) {
        console.log('Loading maxResponses:', survey.maxResponses);
        setMaxResponses(survey.maxResponses);
      }

      if (survey.questionsPerPage) {
        console.log('Loading questionsPerPage:', survey.questionsPerPage);
        // Always force to 1 for clients
        setQuestionsPerPage(1);
      }
      
      // Load profile survey fields
      if (survey.isProfileSurvey) {
        console.log('Loading profile survey data');
        setIsProfileSurvey(true);
        if (survey.profileCategory) {
          console.log('Loading profileCategory:', survey.profileCategory);
          setProfileCategory(survey.profileCategory);
        }
        if (survey.questionMapping) {
          console.log('Loading questionMapping:', survey.questionMapping);
          setQuestionMapping(survey.questionMapping);
        }
      }
      
      // Load public link survey flag
      if (survey.isPublicLink) {
        console.log('Loading public link survey flag:', survey.isPublicLink);
        setIsPublicLink(true);
        setCustomSlug(survey.customSlug || '');
      } else {
        console.log('Survey is not a public link survey');
        setIsPublicLink(false);
      }
      
      // Load public link field
      if (survey.isPublicLink) {
        console.log('Loading public link survey');
        setIsPublicLink(true);
      }

      // Load external survey fields
      if (survey.isExternalSurvey) {
        console.log('Loading external survey data');
        setIsExternalSurvey(true);
        setExternalUrl(survey.externalUrl || '');
      }
      

      if (survey.bitrix) {
        setBitrixConfig({
          ...defaultBitrixConfig,
          ...survey.bitrix,
          contactMapping: {
            ...defaultBitrixConfig.contactMapping,
            ...(survey.bitrix.contactMapping || {})
          },
          managerIds: Array.isArray(survey.bitrix.managerIds) ? survey.bitrix.managerIds : []
        });
      }

      console.log('Survey data loading complete!');
      
    } catch (error) {
      console.error('Error loading survey:', error);
      setError('Не удалось загрузить данные опроса');
    } finally {
      setLoading(false);
    }
  };

  // Determine role once on mount
  useEffect(() => {
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      setIsAdmin(user?.role === 'admin');
    } catch (e) {
      setIsAdmin(false);
    }
  }, []);


  // Check if Bitrix webhook is configured for this client
  useEffect(() => {
    const loadBitrixAvailability = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const { data } = await axios.get(`${API_BASE}/integrations/bitrix`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBitrixAvailable(!!data.configured);
      } catch (e) {
        setBitrixAvailable(false);
      }
    };
    loadBitrixAvailability();
  }, []);




  useEffect(() => {
    if (!bitrixAvailable) return;
    const stepsNow = getBuilderSteps();
    if (activeStep === stepsNow.length - 1 && bitrixConfig.enabled) {
      loadBitrixMeta();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, bitrixAvailable, bitrixConfig.enabled, isProfileSurvey, isPublicLink, isExternalSurvey]);

  const getBuilderSteps = () => {
    const base = isProfileSurvey
      ? profileSurveySteps
      : (isPublicLink
          ? publicLinkSteps
          : (isExternalSurvey ? externalSurveySteps : regularSteps));
    return bitrixAvailable ? [...base, 'Интеграции'] : base;
  };

  const handleNext = () => {
    const steps = getBuilderSteps();
    
    if (activeStep === 0 && !basicInfo.title.trim()) {
      setError('Введите название опроса');
      return;
    }
    if (activeStep === 0 && isExternalSurvey && !externalUrl.trim()) {
      setError('Введите ссылку на внешний опрос');
      return;
    }
    if (activeStep === 1 && !isExternalSurvey && questions.length === 0) {
      setError('Добавьте хотя бы один вопрос');
      return;
    }
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleAddQuestion = () => {
    setEditingQuestion({
      id: `q_${Date.now()}`,
      type: 'single',
      text: '',
      options: [''],
      imageOptions: [],
      required: true,
      allowCustomOption: false,
      randomizeOptions: false,
      order: questions.length
    });
    setQuestionDialogOpen(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion({ ...question });
    setQuestionDialogOpen(true);
  };

  const handleImageUpload = async (event, optionIndex) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/upload/question-image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update the image URL for this option
      const newOptions = [...(editingQuestion.imageOptions || [])];
      newOptions[optionIndex] = { ...newOptions[optionIndex], imageUrl: response.data.imageUrl };
      setEditingQuestion({ ...editingQuestion, imageOptions: newOptions });
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Не удалось загрузить изображение');
    }
  };

  const handleSaveQuestion = () => {
    if (!editingQuestion.text.trim()) {
      setError('Введите текст вопроса');
      return;
    }
    if (['single', 'multiple'].includes(editingQuestion.type) && editingQuestion.options.filter(o => o.trim()).length < 2) {
      setError('Добавьте минимум 2 варианта ответа');
      return;
    }
    if (['single-image', 'multiple-image'].includes(editingQuestion.type)) {
      const validOptions = (editingQuestion.imageOptions || []).filter(o => o.text.trim() && o.imageUrl);
      if (validOptions.length < 2) {
        setError('Добавьте минимум 2 варианта с текстом и изображением');
        return;
      }
    }

    const normalized = normalizeBuilderSelectionLimits(editingQuestion);
    if (normalized.error) {
      setError(normalized.error);
      return;
    }
    const savedQuestion = normalized.question;

    const existing = questions.findIndex(q => q.id === editingQuestion.id);
    if (existing >= 0) {
      const updated = [...questions];
      updated[existing] = savedQuestion;
      setQuestions(updated);
    } else {
      // New question - add to questions list
      setQuestions([...questions, savedQuestion]);
      
      // Automatically add to current page if exists
      if (currentPageId) {
        setPages(pages.map(p =>
          p.id === currentPageId
            ? { ...p, questionIds: [...p.questionIds, savedQuestion.id] }
            : p
        ));
      }
    }
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
    setError('');
  };

  const handleDeleteQuestion = (id) => {
    setQuestions(questions.filter(q => q.id !== id));
    setLogic(logic.filter(l => l.questionId !== id && l.target !== id));
    // Remove from pages
    setPages(pages.map(p => ({
      ...p,
      questionIds: p.questionIds.filter(qId => qId !== id)
    })));
    // Remove from page logic
    setPageLogic(pageLogic.filter(l => l.questionId !== id));
  };

  // Page management functions
  const handleAddPage = () => {
    const newPage = {
      id: `page-${Date.now()}`,
      title: `Страница ${pages.length + 1}`,
      questionIds: []
    };
    setPages([...pages, newPage]);
  };

  const handleDeletePage = (pageId) => {
    if (pages.length === 1) {
      setError('Нельзя удалить единственную страницу');
      return;
    }
    
    const page = pages.find(p => p.id === pageId);
    if (page && page.questionIds.length > 0) {
      if (!window.confirm('На этой странице есть вопросы. Они будут удалены из страницы. Продолжить?')) {
        return;
      }
    }
    
    setPages(pages.filter(p => p.id !== pageId));
    setPageLogic(pageLogic.filter(l => l.targetPageId !== pageId));
    
    // Switch to first page if current page was deleted
    if (currentPageId === pageId) {
      setCurrentPageId(pages[0]?.id || 'page-1');
    }
  };

  const handleMoveQuestionToPage = (questionId, targetPageId) => {
    // Remove from all pages
    const updatedPages = pages.map(p => ({
      ...p,
      questionIds: p.questionIds.filter(qId => qId !== questionId)
    }));
    
    // Add to target page
    const finalPages = updatedPages.map(p =>
      p.id === targetPageId
        ? { ...p, questionIds: [...p.questionIds, questionId] }
        : p
    );
    
    setPages(finalPages);
  };

  const handleAddQuestionToCurrentPage = (questionId) => {
    setPages(pages.map(p =>
      p.id === currentPageId
        ? { ...p, questionIds: [...p.questionIds, questionId] }
        : p
    ));
  };

  // Move question within the same page
  const handleMoveQuestionInPage = (questionId, direction) => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return;
    
    const questionIds = [...currentPage.questionIds];
    const currentIndex = questionIds.indexOf(questionId);
    
    if (currentIndex === -1) return;
    
    if (direction === 'up' && currentIndex > 0) {
      // Swap with previous
      [questionIds[currentIndex - 1], questionIds[currentIndex]] = 
      [questionIds[currentIndex], questionIds[currentIndex - 1]];
    } else if (direction === 'down' && currentIndex < questionIds.length - 1) {
      // Swap with next
      [questionIds[currentIndex], questionIds[currentIndex + 1]] = 
      [questionIds[currentIndex + 1], questionIds[currentIndex]];
    } else {
      return; // No change needed
    }
    
    setPages(pages.map(p =>
      p.id === currentPageId
        ? { ...p, questionIds }
        : p
    ));
  };

  // Функция для получения глобального номера вопроса с учетом всех страниц
  const getGlobalQuestionNumber = (questionId) => {
    let globalIndex = 0;
    for (const page of pages) {
      const indexInPage = page.questionIds.indexOf(questionId);
      if (indexInPage !== -1) {
        return globalIndex + indexInPage + 1;
      }
      globalIndex += page.questionIds.length;
    }
    return 0; // Вопрос не найден на страницах
  };

  const getCurrentPageQuestions = () => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return [];
    
    return currentPage.questionIds
      .map(qId => questions.find(q => q.id === qId))
      .filter(Boolean);
  };

  const getUnplacedQuestions = () => {
    const placedIds = new Set();
    pages.forEach(page => {
      page.questionIds.forEach(id => placedIds.add(id));
    });
    return questions.filter(q => !placedIds.has(q.id));
  };

  const handleMoveQuestion = (index, direction) => {
    const newQuestions = [...questions];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= newQuestions.length) return;
    [newQuestions[index], newQuestions[target]] = [newQuestions[target], newQuestions[index]];
    setQuestions(newQuestions);
  };

  const getQuestionById = (qid) => questions.find(q => q.id === qid);

  const getAnswerOptionsForQuestion = (q) => {
    if (!q) return [];
    // Текстовые варианты ответа
    if (q.type === 'single' || q.type === 'multiple' || q.type === 'dropdown') {
      return (q.options || []).filter(Boolean).map(opt => ({ value: opt, label: opt }));
    }
    if (q.type === 'single-image' || q.type === 'multiple-image') {
      return (q.imageOptions || [])
        .filter(o => o && o.text)
        .map(o => ({ value: o.text, label: o.text }));
    }
    // Шкала Лайкерта (Согласие/несогласие)
    if (q.type === 'likert') {
      const likert = (q.likertOptions && q.likertOptions.length
        ? q.likertOptions
        : ['Полностью не согласен', 'Скорее не согласен', 'Нейтрально', 'Скорее согласен', 'Полностью согласен']);
      return likert.map(opt => ({ value: opt, label: opt }));
    }
    return [];
  };

  const isMultiSelectQuestion = (q) => q && (q.type === 'multiple' || q.type === 'multiple-image');

  const handleAddLogic = () => {
    if (questions.length < 1) return;
    const firstQ = questions[0];
    const defaultTarget = questions[1]?.id || null;
    setLogic([...logic, {
      id: `logic_${Date.now()}`,
      questionId: firstQ.id,
      condition: { answers: [], answer: '', action: 'skip', target: defaultTarget }
    }]);
  };

  const handleUpdateLogic = (id, field, value) => {
    setLogic(logic.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const handleUpdateLogicCondition = (id, updates) => {
    setLogic(logic.map(l => l.id === id ? { ...l, condition: { ...(l.condition || {}), ...updates } } : l));
  };

  const handleDeleteLogic = (id) => {
    setLogic(logic.filter(l => l.id !== id));
  };

  // Page Logic management functions
  const handleAddPageLogic = () => {
    if (questions.length < 1 || pages.length < 1) return;
    const firstQ = questions[0];
    const defaultTargetPage = pages[0]?.id || null;
    setPageLogic([...pageLogic, {
      id: `pageLogic_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      questionId: firstQ.id,
      condition: { answer: '', answers: [] },
      action: 'goToPage',
      targetPageId: defaultTargetPage
    }]);
  };

  const handleUpdatePageLogic = (id, updates) => {
    setPageLogic(pageLogic.map(pl => pl.id === id ? { ...pl, ...updates } : pl));
  };

  const handleUpdatePageLogicCondition = (id, updates) => {
    setPageLogic(pageLogic.map(pl => pl.id === id ? { ...pl, condition: { ...(pl.condition || {}), ...updates } } : pl));
  };

  const handleDeletePageLogic = (id) => {
    setPageLogic(pageLogic.filter(pl => pl.id !== id));
  };

  // Helper to get the page a question belongs to
  const getPageForQuestion = (questionId) => {
    return pages.find(p => p.questionIds.includes(questionId));
  };

  // Загрузка профильных вопросов для динамических квот
  useEffect(() => {
    if (isProfileSurvey) return; // не нужно для профильных опросов
    const fetchProfileQuestions = async () => {
      try {
        setLoadingProfileQuestions(true);
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API_BASE}/surveys/profile/questions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfileQuestions(resp.data.questions || []);
      } catch (err) {
        console.error('Failed to load profile questions for quotas:', err);
      } finally {
        setLoadingProfileQuestions(false);
      }
    };
    fetchProfileQuestions();
  }, [isProfileSurvey]);

  const handleAddQuota = () => {
    setQuotas([...quotas, {
      id: `quota_${Date.now()}`,
      criteria: { dynamic: [] },
      limit: 10,
      filled: 0,
      description: ''
    }]);
  };


  const handleUpdateQuota = (id, field, value) => {
    setQuotas(quotas.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const handleDeleteQuota = (id) => {
    setQuotas(quotas.filter(q => q.id !== id));
  };

  const handleReturnToClient = async () => {
    try {
      setSaving(true);
      setError('');

      if (!surveyId) {
        setError('Невозможно вернуть несуществующий опрос');
        setSaving(false);
        return;
      }

      const token = localStorage.getItem('token');
      
      // Обновляем статус опроса на 'draft', чтобы вернуть его заказчику
      await axios.put(`${API_BASE}/surveys/${surveyId}`, {
        status: 'draft'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate('/admin/moderation');
    } catch (err) {
      console.error('Error returning survey to client:', err);
      setError(err.response?.data?.error || 'Не удалось вернуть опрос заказчику');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (publish = false) => {
    try {
      setSaving(true);
      setError('');

      console.log('handleSubmit: isPublicLink =', isPublicLink, 'isProfileSurvey =', isProfileSurvey);
      console.log('handleSubmit: pageLogic state =', pageLogic);
      console.log('handleSubmit: pages state =', pages);

      // For profile surveys: no quotas required, always active
      if (isProfileSurvey) {
        if (questions.length === 0) {
          setActiveStep(1);
          setError('Добавьте хотя бы один вопрос');
          setSaving(false);
          return;
        }

      const baseSurveyData = {
        ...basicInfo,
        // Map frontend field name to backend field name
        allowMultipleResponses: basicInfo.allowMultipleCompletions || false,
        questions: questions.map((q, idx) => ({ ...q, order: idx })),
        status: 'active', // Profile surveys are always active
        bitrix: bitrixConfig,
        isProfileSurvey: true,
        profileCategory: profileCategory || '',
        questionMapping: questionMapping || [],
        quotas: [], // No quotas for profile surveys
        logic: [], // No logic for profile surveys
        maxResponses: null, // Unlimited responses for profile surveys
        questionsPerPage: 1,
        pages: pages.length > 0 ? pages : undefined,
        pageLogic: pageLogic.length > 0 ? pageLogic : undefined
      };        const surveyData = isAdmin
          ? { ...baseSurveyData, reward: { moneyAmount: 0, pointsAmount: 0, currency: 'BYN' } }
          : baseSurveyData;

        const token = localStorage.getItem('token');
        
        if (surveyId) {
          await axios.put(`${API_BASE}/surveys/${surveyId}`, surveyData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.post(`${API_BASE}/surveys`, surveyData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        // Navigate back to profile surveys list
        navigate('/admin/profile-surveys');
        return;
      }

      // For public link surveys: only need maxResponses, no quotas required
      if (isPublicLink) {
        if (!customSlug || customSlug.length < 3) {
          setActiveStep(0);
          setError('Для опроса по прямой ссылке необходимо указать уникальную ссылку (минимум 3 символа)');
          setSaving(false);
          return;
        }
        if (slugError) {
          setActiveStep(0);
          setError('Пожалуйста, исправьте ошибку в уникальной ссылке');
          setSaving(false);
          return;
        }
        if (questions.length === 0) {
          setActiveStep(2);
          setError('Добавьте хотя бы один вопрос');
          setSaving(false);
          return;
        }
        
        if (!maxResponses || maxResponses <= 0) {
          setActiveStep(3);
          setError('Укажите максимальное количество ответов');
          setSaving(false);
          return;
        }
        
        // Skip quota validation for public link surveys, but keep page logic
        const baseSurveyData = {
          ...basicInfo,
          // Map frontend field name to backend field name
          allowMultipleResponses: basicInfo.allowMultipleCompletions || false,
          questions: questions.map((q, idx) => ({ ...q, order: idx })),
          status: publish ? 'under_review' : 'draft',
          isPublicLink: true,
          customSlug: customSlug || null,
          bitrix: bitrixConfig,
          maxResponses: Number(maxResponses),
          quotas: [], // No quotas for public link surveys
          logic: [], // No old-style logic
          pages: pages.length > 0 ? pages : undefined,
          pageLogic: pageLogic.length > 0 ? pageLogic : undefined // Keep page logic for public surveys
        };
        
        const surveyData = isAdmin
          ? { ...baseSurveyData, reward: { moneyAmount: 0, pointsAmount: 0, currency: 'BYN' }, questionsPerPage }
          : { ...baseSurveyData, questionsPerPage };
        
        const token = localStorage.getItem('token');
        
        if (surveyId) {
          await axios.put(`${API_BASE}/surveys/${surveyId}`, surveyData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (publish) {
            await axios.post(`${API_BASE}/surveys/${surveyId}/publish`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        } else {
          const response = await axios.post(`${API_BASE}/surveys`, surveyData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (publish && response.data.id) {
            await axios.post(`${API_BASE}/surveys/${response.data.id}/publish`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        }
        
        navigate(isAdmin ? '/admin/moderation' : '/client/dashboard');
        return;
      }

      // For external surveys: no questions needed, just basic info + quotas + external URL
      if (isExternalSurvey) {
        if (!externalUrl || !externalUrl.trim()) {
          setActiveStep(0);
          setError('Укажите ссылку на внешний опрос');
          setSaving(false);
          return;
        }
        // Validate quotas
        if (!Array.isArray(quotas) || quotas.length === 0) {
          setActiveStep(1);
          setError('Добавьте хотя бы одну квоту');
          setSaving(false);
          return;
        }
        for (const q of quotas) {
          if (!q.criteria || !Array.isArray(q.criteria.dynamic) || q.criteria.dynamic.length === 0) {
            setActiveStep(1);
            setError('Каждая квота должна содержать хотя бы одно динамическое условие');
            setSaving(false);
            return;
          }
          for (const cond of q.criteria.dynamic) {
            if (!cond.questionId || !Array.isArray(cond.answers) || cond.answers.length === 0) {
              setActiveStep(1);
              setError('Во всех условиях нужно выбрать вопрос и хотя бы один ответ');
              setSaving(false);
              return;
            }
          }
        }
        const quotasTotal = quotas.reduce((sum, q) => sum + (Number(q.limit) || 0), 0);
        if (quotasTotal <= 0) {
          setActiveStep(1);
          setError('Суммарный лимит по квотам должен быть больше 0');
          setSaving(false);
          return;
        }

        const rewardData = {
          moneyAmount: Math.round(Number(reward.moneyAmount || 0) * 10) / 10,
          pointsAmount: Math.round(Number(reward.pointsAmount || 0) * 10) / 10,
          currency: reward.currency || 'BYN'
        };

        let surveyStatus;
        if (publish) {
          surveyStatus = isAdmin ? 'active' : 'under_review';
        } else if (isAdmin) {
          surveyStatus = surveyId && loadedSurvey?.status ? loadedSurvey.status : 'pending';
        } else {
          surveyStatus = 'draft';
        }

        const totalMaxResponses = resolveMaxResponses();

        const baseSurveyData = {
          ...basicInfo,
          allowMultipleResponses: basicInfo.allowMultipleCompletions || false,
          questions: [],
          logic: [],
          quotas,
          maxResponses: totalMaxResponses,
          status: surveyStatus,
          isExternalSurvey: true,
          externalUrl: externalUrl.trim(),
          bitrix: bitrixConfig,
          isProfileSurvey: false,
          isPublicLink: false,
          pages: [],
          pageLogic: []
        };

        const surveyData = isAdmin
          ? { ...baseSurveyData, reward: rewardData, questionsPerPage: 1 }
          : { ...baseSurveyData, questionsPerPage: 1 };

        const token = localStorage.getItem('token');

        if (surveyId) {
          await axios.put(`${API_BASE}/surveys/${surveyId}`, surveyData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (publish) {
            await axios.post(`${API_BASE}/surveys/${surveyId}/publish`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        } else {
          const response = await axios.post(`${API_BASE}/surveys`, surveyData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (publish && response.data.id) {
            await axios.post(`${API_BASE}/surveys/${response.data.id}/publish`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        }

        navigate(isAdmin ? '/admin/moderation' : '/client/dashboard');
        return;
      }

      // Regular survey validation: must have at least one quota
      if (!Array.isArray(quotas) || quotas.length === 0) {
        setActiveStep(3);
        setError('Добавьте хотя бы одну квоту');
        setSaving(false);
        return;
      }
      // Validate dynamic conditions
      for (const q of quotas) {
        if (!q.criteria || !Array.isArray(q.criteria.dynamic) || q.criteria.dynamic.length === 0) {
          setActiveStep(3);
          setError('Каждая квота должна содержать хотя бы одно динамическое условие');
          setSaving(false);
          return;
        }
        for (const cond of q.criteria.dynamic) {
          if (!cond.questionId || !Array.isArray(cond.answers) || cond.answers.length === 0) {
            setActiveStep(3);
            setError('Во всех условиях нужно выбрать вопрос и хотя бы один ответ');
            setSaving(false);
            return;
          }
        }
      }
      // Доп. проверка: сумма лимитов квот должна быть > 0
      const quotasTotal = quotas.reduce((sum, q) => sum + (Number(q.limit) || 0), 0);
      if (quotasTotal <= 0) {
        setActiveStep(3);
        setError('Суммарный лимит по квотам должен быть больше 0');
        setSaving(false);
        return;
      }

      // sanitize logic on client: drop rules without answers/target
      const cleanedLogic = (logic || []).map(rule => {
        const q = getQuestionById(rule.questionId);
        const opts = getAnswerOptionsForQuestion(q).map(o => o.value);
        const answersArr = Array.isArray(rule.condition?.answers)
          ? rule.condition.answers
          : (rule.condition?.answer ? [rule.condition.answer] : []);
        const filteredAnswers = answersArr.filter(a => opts.includes(a));
        const action = rule.condition?.action || 'skip';
        const target = action === 'end' ? null : (rule.condition?.target || null);
        return {
          id: rule.id,
          questionId: rule.questionId,
          condition: {
            answers: filteredAnswers,
            action,
            target
          }
        };
      }).filter(rule => {
        if (!rule.questionId) return false;
        const q = getQuestionById(rule.questionId);
        if (!q) return false;
        const hasAnswers = Array.isArray(rule.condition.answers) && rule.condition.answers.length > 0;
        if (!hasAnswers) return false;
        if (rule.condition.action !== 'end' && !rule.condition.target) return false;
        return true;
      });

      // Рассчитываем максимальное количество ответов из квот
      // Админ при редактировании существующего опроса задаёт лимит сам, сумма квот — только подсказка
      const totalMaxResponses = resolveMaxResponses();
      
      // Объект награды формируется только администратором
      const rewardData = {
        moneyAmount: Math.round(Number(reward.moneyAmount || 0) * 10) / 10,
        pointsAmount: Math.round(Number(reward.pointsAmount || 0) * 10) / 10,
        currency: reward.currency || 'BYN'
      };

      // Определяем статус: 
      // - админ + publish=true → 'active'
      // - клиент + publish=true → 'under_review' (отправка на модерацию)
      // - админ без publish → сохраняем существующий статус или 'pending' для нового
      // - клиент без publish → 'draft'
      let surveyStatus;
      if (publish) {
        surveyStatus = isAdmin ? 'active' : 'under_review';
      } else if (isAdmin) {
        // Админ сохраняет без публикации: сохраняем текущий статус или 'pending' для нового
        surveyStatus = surveyId && loadedSurvey?.status ? loadedSurvey.status : 'pending';
      } else {
        surveyStatus = 'draft';
      }

      const baseSurveyData = {
        ...basicInfo,
        // Map frontend field name to backend field name
        allowMultipleResponses: basicInfo.allowMultipleCompletions || false,
        targetAudience,
        questions: questions.map((q, idx) => ({ ...q, order: idx })),
        logic: cleanedLogic,
        quotas,
        maxResponses: totalMaxResponses,
        status: surveyStatus,
        // Profile survey fields
        isProfileSurvey,
        // Public link survey
        isPublicLink,
        // Page-based structure
        pages: pages.length > 0 ? pages : [],
        pageLogic: pageLogic.length > 0 ? pageLogic : [],
        bitrix: bitrixConfig
      };

      console.log('Saving survey with pageLogic:', pageLogic.length, pageLogic);
      console.log('baseSurveyData.pageLogic:', baseSurveyData.pageLogic);

      const surveyData = isAdmin
        ? { ...baseSurveyData, reward: rewardData, questionsPerPage, ...(surveyId ? { interviewerQuotaMapping } : {}) }
        : { ...baseSurveyData, questionsPerPage };

      console.log('Full surveyData to send:', JSON.stringify({
        ...surveyData,
        pages: surveyData.pages?.length || 0,
        pageLogic: surveyData.pageLogic?.length || 0,
        questions: surveyData.questions?.length || 0
      }));
      console.log('surveyData.pageLogic:', surveyData.pageLogic);

      const token = localStorage.getItem('token');
      
      let response;
      if (surveyId) {
        // Обновление существующего опроса
        response = await axios.put(`${API_BASE}/surveys/${surveyId}`, surveyData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (publish) {
          await axios.post(`${API_BASE}/surveys/${surveyId}/publish`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } else {
        // Создание нового опроса
        response = await axios.post(`${API_BASE}/surveys`, surveyData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (publish && response.data.id) {
          await axios.post(`${API_BASE}/surveys/${response.data.id}/publish`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }

      // Навигация после сохранения: админ переходит на модерацию, заказчик на дашборд
      if (isAdmin) {
        navigate('/admin/moderation');
      } else {
        navigate('/client/dashboard');
      }
    } catch (err) {
      console.error('Survey save error:', err);
      setError(err.response?.data?.error || (surveyId ? 'Не удалось обновить опрос' : 'Не удалось создать опрос'));
    } finally {
      setSaving(false);
    }
  };

  // Render functions for each step
  const renderBasicInfo = () => (
          <Box>
            <TextField
              fullWidth
              label="Название опроса"
              value={basicInfo.title}
              onChange={(e) => setBasicInfo({ ...basicInfo, title: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Описание"
              value={basicInfo.description}
              onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            
            {/* Profile Survey Toggle */}
            {isAdmin && (
              <Paper sx={{ p: 2, mt: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isProfileSurvey}
                      onChange={(e) => {
                        setIsProfileSurvey(e.target.checked);
                      }}
                      sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                    />
                  }
                  label="Профильный опрос (обязательный для заполнения профиля)"
                />
                
                {isProfileSurvey && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Профильные опросы заполняются респондентами первыми. Вопросы и ответы автоматически становятся динамическими полями профиля пользователя.
                  </Alert>
                )}
              </Paper>
            )}
            
            {/* Public Link Survey Toggle */}
            {!isProfileSurvey && !isExternalSurvey && (
              <Paper sx={{ p: 2, mt: 2, mb: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPublicLink}
                      onChange={(e) => setIsPublicLink(e.target.checked)}
                      sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                    />
                  }
                  label="Опрос по прямой ссылке (доступен любому без регистрации)"
                />
                
                {isPublicLink && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Опросы по прямой ссылке не выдаются зарегистрированным респондентам. Любой у кого есть ссылка сможет пройти опрос без авторизации. Награды не начисляются.
                    </Alert>
                    <TextField
                      fullWidth
                      label="Уникальная ссылка на опрос"
                      value={customSlug}
                      onChange={async (e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setCustomSlug(value);
                        if (value.length >= 3) {
                          setCheckingSlug(true);
                          setSlugError('');
                          try {
                            const token = localStorage.getItem('token');
                            const response = await axios.get(`${API_BASE}/surveys/check-slug/${value}`, {
                              headers: { Authorization: `Bearer ${token}` },
                              params: { excludeId: surveyId }
                            });
                            if (!response.data.available) {
                              setSlugError('Эта ссылка уже занята');
                            }
                          } catch (err) {
                            console.error('Error checking slug:', err);
                          } finally {
                            setCheckingSlug(false);
                          }
                        } else {
                          setSlugError(value.length > 0 ? 'Минимум 3 символа' : '');
                        }
                      }}
                      error={Boolean(slugError)}
                      helperText={slugError || (customSlug ? `Ссылка: ${window.location.origin}/public/survey/${customSlug}` : 'Используйте только латинские буквы, цифры и дефис (минимум 3 символа)')}
                      placeholder="my-awesome-survey"
                      InputProps={{
                        endAdornment: checkingSlug && <CircularProgress size={20} />
                      }}
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                )}
              </Paper>
            )}

            {/* External Survey Toggle */}
            {!isProfileSurvey && !isPublicLink && (
              <Paper sx={{ p: 2, mt: 2, mb: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isExternalSurvey}
                      onChange={(e) => setIsExternalSurvey(e.target.checked)}
                      sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                    />
                  }
                  label="Внешний опрос (переход по внешней ссылке)"
                />
                
                {isExternalSurvey && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Респондент увидит этот опрос в каталоге. При нажатии «Перейти к опросу» он будет перенаправлен на внешнюю ссылку, а награда начислится автоматически.
                    </Alert>
                    <TextField
                      fullWidth
                      label="Ссылка на внешний опрос"
                      value={externalUrl}
                      onChange={(e) => setExternalUrl(e.target.value)}
                      placeholder="https://example.com/survey/{user_id}"
                      helperText="Полная ссылка (включая https://). Используйте {user_id} для подстановки ID респондента, например: https://example.com/survey/{user_id}"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                )}
              </Paper>
            )}
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Категория</InputLabel>
              <Select
                value={basicInfo.category}
                label="Категория"
                onChange={(e) => setBasicInfo({ ...basicInfo, category: e.target.value })}
              >
                <MenuItem value="online-surveys">Онлайн-опросы</MenuItem>
                <MenuItem value="online-interviews">Онлайн-интервью и фокус-группы</MenuItem>
                <MenuItem value="website-app-testing">Тест сайтов и приложений</MenuItem>
                <MenuItem value="store-visits">Визиты в магазины и офисы</MenuItem>
                <MenuItem value="product-testing">Тестирование продуктов и услуг</MenuItem>
                <MenuItem value="other">Другое</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Предполагаемое время (мин)"
              type="number"
              value={basicInfo.estimatedTime}
              onChange={(e) => setBasicInfo({ ...basicInfo, estimatedTime: parseInt(e.target.value) || 1 })}
              margin="normal"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={basicInfo.allowMultipleCompletions || false}
                  onChange={(e) => setBasicInfo({ ...basicInfo, allowMultipleCompletions: e.target.checked })}
                />
              }
              label="Разрешить прохождение несколько раз одним пользователем"
              sx={{ mt: 2 }}
            />
          </Box>
        );

  const renderQuestions = () => {
    const currentPageQuestions = getCurrentPageQuestions();
    const unplacedQuestions = getUnplacedQuestions();

    return (
      <Grid container spacing={2}>
        {/* Left: Constructor */}
        <Grid item xs={12} md={8}>
          <Box>
        <Typography variant="h6" gutterBottom>Вопросы опроса</Typography>

        {/* Page Management Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={pageHeaderRowSx}>
            <Typography variant="subtitle1" fontWeight="medium">Страницы опроса</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddPage}
            >
              Добавить страницу
            </Button>
          </Box>

          {/* Page Tabs */}
          {pages.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={currentPageId || pages[0]?.id}
                onChange={(e, newValue) => setCurrentPageId(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {pages.map((page, index) => (
                  <Tab
                    key={page.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Страница {index + 1} ({page.questionIds.length})</span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePage(page.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    value={page.id}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {pages.length === 0 && (
            <Alert severity="warning">
              Страницы не созданы. Нажмите "Добавить страницу" для создания постраничной структуры опроса.
            </Alert>
          )}
        </Paper>

        {/* Current Page Questions */}
        {currentPageId && currentPageQuestions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Вопросы на текущей странице
            </Typography>
            {currentPageQuestions.map((question, index) => (
              <Card key={question.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {getGlobalQuestionNumber(question.id)}. {question.text}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Тип: {
                          question.type === 'single' ? 'Одиночный выбор' :
                          question.type === 'multiple' ? 'Множественный выбор' :
                          question.type === 'text' ? 'Текстовый ответ' :
                          question.type === 'rating' ? 'Рейтинг' :
                          question.type === 'single-image' ? 'Одиночный выбор (с картинками)' :
                          question.type === 'multiple-image' ? 'Множественный выбор (с картинками)' :
                          question.type === 'dropdown' ? 'Выпадающий список' :
                          question.type === 'ranking' ? 'Ранжирование' :
                          question.type === 'likert' ? 'Шкала Лайкерта' :
                          question.type === 'matrix' ? 'Матрица' :
                          question.type === 'nps' ? 'NPS' :
                          question.type === 'scale' ? 'Числовая шкала' :
                          question.type
                        }
                        {question.required && ' • Обязательный'}
                        {selectionCardSuffix(question)}
                      </Typography>
                      {(question.type === 'single' || question.type === 'multiple' || question.type === 'dropdown') && question.options && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Варианты: {question.options.slice(0, 3).join(', ')}
                          {question.options.length > 3 && '...'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Box sx={questionActionsRowSx}>
                          {/* Move up/down within page */}
                          {index > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => handleMoveQuestionInPage(question.id, 'up')}
                              title="Переместить вверх"
                            >
                              <UpIcon />
                            </IconButton>
                          )}
                          {index < currentPageQuestions.length - 1 && (
                            <IconButton
                              size="small"
                              onClick={() => handleMoveQuestionInPage(question.id, 'down')}
                              title="Переместить вниз"
                            >
                              <DownIcon />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditQuestion(question)}
                            title="Редактировать"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteQuestion(question.id)}
                            title="Удалить"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        {/* Move to other page */}
                        {pages.length > 1 && (
                          <FormControl size="small" sx={movePageFormSx}>
                            <InputLabel>Переместить</InputLabel>
                            <Select
                              value=""
                              label="Переместить"
                              onChange={(e) => {
                                handleMoveQuestionToPage(question.id, e.target.value);
                              }}
                            >
                              {pages.filter(p => p.id !== currentPageId).map((page, idx) => (
                                <MenuItem key={page.id} value={page.id}>
                                  → Страница {pages.findIndex(p2 => p2.id === page.id) + 1}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Unplaced Questions */}
        {unplacedQuestions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Вопросы без страницы ({unplacedQuestions.length})
              </Typography>
              <Typography variant="body2">
                Эти вопросы не добавлены ни на одну страницу. Добавьте их на страницу для использования в опросе.
              </Typography>
            </Alert>
            {unplacedQuestions.map((question, index) => (
              <Card key={question.id} sx={{ mb: 2, bgcolor: 'warning.light' }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs>
                      <Typography variant="subtitle1" fontWeight="medium">
                        [Не размещен] {question.text}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Тип: {
                          question.type === 'single' ? 'Одиночный выбор' :
                          question.type === 'multiple' ? 'Множественный выбор' :
                          question.type === 'text' ? 'Текстовый ответ' :
                          question.type
                        }
                        {selectionCardSuffix(question)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Box sx={questionActionsRowSx}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditQuestion(question)}
                            title="Редактировать"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteQuestion(question.id)}
                            title="Удалить"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        {/* Add to page */}
                        {pages.length > 0 && (
                          <FormControl size="small" sx={movePageFormSx}>
                            <InputLabel>Добавить на</InputLabel>
                            <Select
                              value=""
                              label="Добавить на"
                              onChange={(e) => {
                                handleMoveQuestionToPage(question.id, e.target.value);
                              }}
                            >
                              {pages.map((page, idx) => (
                                <MenuItem key={page.id} value={page.id}>
                                  → Страница {idx + 1}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* All Questions List (for reference) */}
        {questions.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Нет вопросов. Добавьте первый вопрос для опроса.
          </Alert>
        )}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddQuestion}
          fullWidth
          size="large"
        >
          Добавить вопрос
        </Button>
          </Box>
        </Grid>

        {/* Right: Live Preview */}
        <Grid item xs={12} md={4}>
          <Box sx={stickyPreviewSx}>
            <LivePreview
              survey={basicInfo}
              questions={questions}
              pages={pages}
              pageLogic={pageLogic}
              currentPageId={currentPageId}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderLogic = () => {
    return null; // Will use inline JSX from switch case
  };

  // Обработчики динамических условий в квотах
  const handleAddDynamicCondition = (quotaId) => {
    setQuotas(quotas.map(q => q.id === quotaId
      ? { ...q, criteria: { ...q.criteria, dynamic: [...(q.criteria?.dynamic || []), { id: `cond_${Date.now()}_${Math.random().toString(36).slice(2,8)}`, questionId: '', answers: [] }] } }
      : q
    ));
  };

  const handleUpdateDynamicCondition = (quotaId, conditionId, updates) => {
    setQuotas(quotas.map(q => {
      if (q.id !== quotaId) return q;
      const updatedDyn = (q.criteria?.dynamic || []).map(c => c.id === conditionId ? { ...c, ...updates } : c);
      return { ...q, criteria: { ...q.criteria, dynamic: updatedDyn } };
    }));
  };

  const handleDeleteDynamicCondition = (quotaId, conditionId) => {
    setQuotas(quotas.map(q => q.id === quotaId
      ? { ...q, criteria: { ...q.criteria, dynamic: (q.criteria?.dynamic || []).filter(c => c.id !== conditionId) } }
      : q
    ));
  };

  const renderQuotas = () => null;


  const loadBitrixMeta = async () => {
    if (!bitrixAvailable) return;
    try {
      setBitrixMetaLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [managersRes, categoriesRes] = await Promise.all([
        axios.get(`${API_BASE}/integrations/bitrix/managers`, { headers }).catch(() => ({ data: { managers: [] } })),
        axios.get(`${API_BASE}/integrations/bitrix/deal-categories`, { headers }).catch(() => ({ data: { categories: [] } }))
      ]);
      setBitrixManagers(managersRes.data?.managers || []);
      setBitrixCategories(categoriesRes.data?.categories || []);
    } catch (e) {
      console.warn('Bitrix meta load failed', e);
    } finally {
      setBitrixMetaLoading(false);
    }
  };

  const updateBitrixConfig = (patch) => {
    setBitrixConfig((prev) => ({ ...prev, ...patch }));
  };

  const renderBitrixIntegrationStep = () => {
    const textQuestions = (questions || []).filter((q) => q && q.id && q.text);
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Интеграция с Битрикс24</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Можно пропустить этот шаг. Если включите отправку, ответы будут создавать лиды или сделки в вашем Битрикс24.
        </Alert>

        <FormControlLabel
          control={
            <Switch
              checked={!!bitrixConfig.enabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                updateBitrixConfig({ enabled });
                if (enabled) loadBitrixMeta();
              }}
            />
          }
          label="Отправлять ответы в Битрикс24"
          sx={{ mb: 2 }}
        />

        {!bitrixConfig.enabled ? (
          <Typography variant="body2" color="text.secondary">
            Интеграция отключена для этого опроса. Нажмите «Далее» / сохраните опрос как обычно.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Тип сущности</InputLabel>
                <Select
                  label="Тип сущности"
                  value={bitrixConfig.entityType || 'lead'}
                  onChange={(e) => updateBitrixConfig({ entityType: e.target.value })}
                >
                  <MenuItem value="lead">Лид</MenuItem>
                  <MenuItem value="deal">Сделка</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {bitrixConfig.entityType === 'deal' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Воронка</InputLabel>
                  <Select
                    label="Воронка"
                    value={bitrixConfig.categoryId || ''}
                    onChange={(e) => updateBitrixConfig({ categoryId: e.target.value || null })}
                    disabled={bitrixMetaLoading}
                  >
                    <MenuItem value="">Не выбрана</MenuItem>
                    {bitrixCategories.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Назначение менеджеров</InputLabel>
                <Select
                  label="Назначение менеджеров"
                  value={bitrixConfig.assignmentMode || 'single'}
                  onChange={(e) => updateBitrixConfig({ assignmentMode: e.target.value })}
                >
                  <MenuItem value="single">Один менеджер</MenuItem>
                  <MenuItem value="round_robin">По очереди</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Менеджеры</InputLabel>
                <Select
                  multiple
                  label="Менеджеры"
                  value={bitrixConfig.managerIds || []}
                  onChange={(e) => updateBitrixConfig({ managerIds: e.target.value })}
                  renderValue={(selected) => selected.map((id) => {
                    const m = bitrixManagers.find((x) => String(x.id) === String(id));
                    return m ? m.name : id;
                  }).join(', ')}
                  disabled={bitrixMetaLoading}
                >
                  {bitrixManagers.map((m) => (
                    <MenuItem key={m.id} value={String(m.id)}>
                      <Checkbox checked={(bitrixConfig.managerIds || []).map(String).includes(String(m.id))} />
                      <ListItemText primary={m.name} secondary={m.email || undefined} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название лида / сделки"
                value={bitrixConfig.titleTemplate || ''}
                onChange={(e) => updateBitrixConfig({ titleTemplate: e.target.value })}
                helperText="Плейсхолдеры: {fullName}, {phone}, {email}, {surveyTitle}"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                Сопоставление контактов из вопросов (необязательно)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Если у респондента нет аккаунта (публичный/внешний опрос) или нужно переопределить поля — выберите вопросы.
              </Typography>
            </Grid>

            {[
              ['fullNameQuestionId', 'Вопрос с ФИО'],
              ['phoneQuestionId', 'Вопрос с телефоном'],
              ['emailQuestionId', 'Вопрос с email']
            ].map(([key, label]) => (
              <Grid item xs={12} md={4} key={key}>
                <FormControl fullWidth>
                  <InputLabel>{label}</InputLabel>
                  <Select
                    label={label}
                    value={(bitrixConfig.contactMapping && bitrixConfig.contactMapping[key]) || ''}
                    onChange={(e) => updateBitrixConfig({
                      contactMapping: {
                        ...(bitrixConfig.contactMapping || {}),
                        [key]: e.target.value || null
                      }
                    })}
                  >
                    <MenuItem value="">Не задано</MenuItem>
                    {textQuestions.map((q) => (
                      <MenuItem key={q.id} value={q.id}>
                        {(q.text || '').substring(0, 60)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bitrixConfig.includeAnswersInComments !== false}
                    onChange={(e) => updateBitrixConfig({ includeAnswersInComments: e.target.checked })}
                  />
                }
                label="Добавлять ответы на вопросы в комментарий заявки"
              />
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };


  const renderStepContent = () => {
    const stepsNow = getBuilderSteps();
    if (bitrixAvailable && activeStep === stepsNow.length - 1) {
      return renderBitrixIntegrationStep();
    }

    if (isProfileSurvey) {
      switch (activeStep) {
        case 0: // Базовая информация
          return renderBasicInfo();
        case 1: // Вопросы
          return renderQuestions();
        case 2: // Логика и ветвления
          return (
            <Grid container spacing={2}>
              {/* Left: Logic Editor */}
              <Grid item xs={12} md={8}>
                <Box>
              <Typography variant="h6" gutterBottom>Логика переходов</Typography>
              
              {/* Page-based logic */}
              {pages.length > 0 ? (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Настройте условные переходы между страницами на основе ответов респондента. При выборе определенного ответа респондент перейдет на указанную страницу или опрос завершится.
                  </Alert>
                  
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    onClick={handleAddPageLogic} 
                    sx={{ mb: 2 }}
                    disabled={questions.length === 0 || pages.length < 1}
                  >
                    Добавить правило
                  </Button>

                  {pageLogic.length === 0 && (
                    <Alert severity="info">
                      Нет правил логики. Без правил респонденты будут переходить последовательно через все страницы.
                    </Alert>
                  )}

                  {pageLogic.map((rule) => {
                    const question = getQuestionById(rule.questionId);
                    const questionPage = getPageForQuestion(rule.questionId);
                    const options = getAnswerOptionsForQuestion(question);
                    const disabled = options.length === 0;

                    return (
                      <Card key={rule.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2} alignItems="center">
                            {/* Question selector */}
                            <Grid item xs={12} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Вопрос</InputLabel>
                                <Select
                                  value={rule.questionId}
                                  label="Вопрос"
                                  onChange={(e) => {
                                    const newQId = e.target.value;
                                    const defaultTarget = pages[0]?.id || null;
                                    handleUpdatePageLogic(rule.id, {
                                      questionId: newQId,
                                      condition: { answers: [], answer: '' },
                                      action: 'goToPage',
                                      targetPageId: defaultTarget
                                    });
                                  }}
                                >
                                  {questions.map((q, i) => {
                                    const qPage = getPageForQuestion(q.id);
                                    const pageNumber = qPage ? pages.findIndex(p => p.id === qPage.id) + 1 : null;
                                    return (
                                      <MenuItem key={q.id} value={q.id}>
                                        {i + 1}. {q.text.substring(0, 25)}... 
                                        {pageNumber && ` (Стр. ${pageNumber})`}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Answer selector */}
                            <Grid item xs={12} sm={3}>
                              <FormControl fullWidth size="small" disabled={disabled}>
                                <InputLabel>Если ответ</InputLabel>
                                <Select
                                  multiple={isMultiSelectQuestion(question)}
                                  value={Array.isArray(rule.condition?.answers) && rule.condition.answers.length > 0 
                                    ? rule.condition.answers 
                                    : (rule.condition?.answer ? [rule.condition.answer] : [])}
                                  label="Если ответ"
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const arr = Array.isArray(val) ? val : [val];
                                    handleUpdatePageLogicCondition(rule.id, { 
                                      answers: arr, 
                                      answer: arr[0] || '' 
                                    });
                                  }}
                                  renderValue={(selected) => (selected || []).join(', ')}
                                >
                                  {options.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Action selector - goToPage or end */}
                            <Grid item xs={12} sm={2}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Действие</InputLabel>
                                <Select
                                  value={rule.action || 'goToPage'}
                                  label="Действие"
                                  onChange={(e) => {
                                    const newAction = e.target.value;
                                    handleUpdatePageLogic(rule.id, { 
                                      action: newAction,
                                      targetPageId: newAction === 'end' ? null : (rule.targetPageId || pages[0]?.id)
                                    });
                                  }}
                                >
                                  <MenuItem value="goToPage">Перейти к странице</MenuItem>
                                  <MenuItem value="end">Завершить опрос</MenuItem>
                                  <MenuItem value="endWithoutPayment">Завершить без оплаты</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Target page selector */}
                            <Grid item xs={12} sm={3}>
                              {rule.action !== 'end' && rule.action !== 'endWithoutPayment' && (
                                <FormControl fullWidth size="small">
                                  <InputLabel>Целевая страница</InputLabel>
                                  <Select
                                    value={rule.targetPageId || ''}
                                    label="Целевая страница"
                                    onChange={(e) => handleUpdatePageLogic(rule.id, { targetPageId: e.target.value })}
                                  >
                                    {pages.map((page, idx) => (
                                      <MenuItem key={page.id} value={page.id}>
                                        Страница {idx + 1} ({page.questionIds.length} вопр.)
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                              {rule.action === 'end' && (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value="—"
                                  disabled
                                  label="Целевая страница"
                                />
                              )}
                            </Grid>

                            {/* Delete button */}
                            <Grid item xs={12} sm={1}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeletePageLogic(rule.id)} 
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>

                          {/* Disqualification reason input */}
                          {rule.action === 'endWithoutPayment' && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Причина завершения без оплаты"
                              placeholder="Введите текст, который увидит респондент при дисквалификации"
                              value={rule.disqualificationReason || ''}
                              onChange={(e) => handleUpdatePageLogic(rule.id, { disqualificationReason: e.target.value })}
                              multiline
                              rows={2}
                              sx={{ mt: 2 }}
                              helperText="Оставьте пустым для отображения стандартного сообщения"
                            />
                          )}

                          {/* Warning for questions without options */}
                          {options.length === 0 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                              Логика по ответам доступна только для вопросов с вариантами: одиночный, множественный, выпадающий список, варианты с картинками, а также шкала Лайкерта.
                            </Alert>
                          )}

                          {/* Show which page the question is on */}
                          {questionPage && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              Вопрос находится на странице: <strong>Страница {pages.findIndex(p => p.id === questionPage.id) + 1}</strong>
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Paper>
              ) : (
                <Alert severity="warning">
                  Создайте страницы на шаге "Вопросы" для настройки логики переходов.
                </Alert>
              )}
                </Box>
              </Grid>

              {/* Right: Live Preview */}
              <Grid item xs={12} md={4}>
                <Box sx={stickyPreviewSx}>
                  <LivePreview
                    survey={basicInfo}
                    questions={questions}
                    pages={pages}
                    pageLogic={pageLogic}
                    currentPageId={currentPageId}
                  />
                </Box>
              </Grid>
            </Grid>
          );
        default:
          return null;
      }
    }

    // External surveys (only 2 steps: BasicInfo + Quotas)
    if (isExternalSurvey) {
      switch (activeStep) {
        case 0:
          return renderBasicInfo();
        case 1: // Квоты (same as regular step 3)
          return (
            <Box>
              {isAdmin && (
                <>
                  <Typography variant="h6" gutterBottom>Награды</Typography>
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Деньги (BYN)"
                          type="number"
                          value={reward.moneyAmount || 0}
                          onChange={(e) => setReward({ ...reward, moneyAmount: parseFloat(e.target.value) || 0 })}
                          InputProps={{ endAdornment: 'BYN' }}
                          inputProps={{ step: 0.1, min: 0 }}
                          helperText="За переход по ссылке (с точностью до 0.1)"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Подарочные баллы"
                          type="number"
                          value={reward.pointsAmount || 0}
                          onChange={(e) => setReward({ ...reward, pointsAmount: parseFloat(e.target.value) || 0 })}
                          InputProps={{ endAdornment: 'баллов' }}
                          inputProps={{ step: 0.1, min: 0 }}
                          helperText="За переход по ссылке (с точностью до 0.1)"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </>
              )}

              <Typography variant="h6" gutterBottom>Динамический конструктор квот</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Формируйте квоты на основе ответов профильных опросов. В каждой квоте добавьте одно или несколько условий (вопрос + допустимые ответы). Респондент должен удовлетворять всем условиям квоты.
              </Alert>
              <Alert severity="success" sx={{ mb: 2 }}>
                * Если Вы хотите опубликовать опрос для всех пользователей, используйте условие "Ваш пол" и прокликайте оба ответа, и "Мужчина" и "Женщина". И выставьте количество ответов. Это условие подойдёт для всех пользователей платформы.
              </Alert>
              {loadingProfileQuestions && <Alert severity="info" sx={{ mb:2 }}>Загрузка профильных вопросов...</Alert>}
              {!loadingProfileQuestions && profileQuestions.length === 0 && (
                <Alert severity="warning" sx={{ mb:2 }}>Нет активных профильных вопросов с вариантами ответов. Создайте профильные опросы сначала.</Alert>
              )}
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddQuota} sx={{ mb: 2 }} disabled={profileQuestions.length === 0}>
                Добавить квоту
              </Button>

              {quotas.map((quota) => (
                <Card key={quota.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Описание квоты"
                          value={quota.description || ''}
                          onChange={(e) => handleUpdateQuota(quota.id, 'description', e.target.value)}
                          placeholder="Например: Женщины купившие X товар"
                        />
                      </Grid>
                      {/* Dynamic conditions list */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Условия квоты</Typography>
                        {(quota.criteria.dynamic || []).length === 0 && (
                          <Alert severity="warning" sx={{ mb:1 }}>Нет условий. Добавьте хотя бы одно условие.</Alert>
                        )}
                        {(quota.criteria.dynamic || []).map(cond => {
                          const question = profileQuestions.find(q => q.questionId === cond.questionId);
                          const availableAnswers = question ? question.options : [];
                          return (
                            <Paper key={cond.id} sx={{ p:2, mb:1, bgcolor:'#fafafa' }} variant="outlined">
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} md={5}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Вопрос профиля</InputLabel>
                                    <Select
                                      value={cond.questionId || ''}
                                      label="Вопрос профиля"
                                      onChange={(e) => handleUpdateDynamicCondition(quota.id, cond.id, { questionId: e.target.value, answers: [] })}
                                    >
                                      {profileQuestions.map(pq => (
                                        <MenuItem key={pq.questionId} value={pq.questionId}>
                                          {pq.surveyTitle}: {pq.questionText.substring(0,60)}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <FormControl fullWidth size="small" disabled={!cond.questionId}>
                                    <InputLabel>Допустимые ответы</InputLabel>
                                    <Select
                                      multiple
                                      value={cond.answers || []}
                                      label="Допустимые ответы"
                                      onChange={(e) => handleUpdateDynamicCondition(quota.id, cond.id, { answers: e.target.value })}
                                      renderValue={(selected) => (selected || []).join(', ')}
                                    >
                                      {availableAnswers.map(opt => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <IconButton color="error" size="small" onClick={() => handleDeleteDynamicCondition(quota.id, cond.id)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Paper>
                          );
                        })}
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          variant="outlined"
                          disabled={profileQuestions.length === 0}
                          onClick={() => handleAddDynamicCondition(quota.id)}
                        >
                          Добавить условие
                        </Button>
                      </Grid>

                      {/* Quota limit */}
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Лимит"
                          type="number"
                          value={quota.limit}
                          onChange={(e) => handleUpdateQuota(quota.id, 'limit', parseInt(e.target.value) || 1)}
                          helperText="Макс. ответов"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Заполнено"
                          type="number"
                          value={quota.filled || 0}
                          disabled
                          helperText="Текущее"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteQuota(quota.id)}>
                      Удалить квоту
                    </Button>
                  </CardActions>
                </Card>
              ))}

              {quotas.length === 0 && (
                <Alert severity="error">Добавьте хотя бы одну квоту</Alert>
              )}

              {isAdmin && surveyId && quotas.length > 0 && renderInterviewerQuotaMapping()}

              {quotas.length > 0 && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2">Сводка по квотам</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Всего респондентов: {quotas.reduce((sum, q) => sum + q.limit, 0)}
                  </Typography>
                  {isAdminEditingExisting() && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        type="number"
                        size="small"
                        label="Общий лимит ответов (админ)"
                        value={maxResponses}
                        onChange={(e) => setMaxResponses(parseInt(e.target.value, 10) || 0)}
                        inputProps={{ min: 1 }}
                        helperText={`Сумма квот — ${quotasLimitSum()}, это подсказка. Сохраняется значение из этого поля. Собрано: ${loadedSurvey?.responseCount || 0}`}
                      />
                    </Box>
                  )}
                  {isAdmin && Number(reward.moneyAmount || 0) > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Итоговая стоимость</Typography>
                      <Typography variant="h6">{(Number(reward.moneyAmount || 0) * quotas.reduce((sum, q) => sum + q.limit, 0)).toFixed(2)} BYN</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {quotas.reduce((sum, q) => sum + q.limit, 0)} респондентов × {Number(reward.moneyAmount || 0)} BYN
                      </Typography>
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          );
        default:
          return null;
      }
    }

    // Public link surveys
    if (isPublicLink) {
      switch (activeStep) {
        case 0: // Базовая информация
          return renderBasicInfo();
        case 1: // Вопросы
          return renderQuestions();
        case 2: // Логика и ветвления (такая же как для обычных опросов)
          return (
            <Grid container spacing={2}>
              {/* Left: Logic Editor */}
              <Grid item xs={12} md={8}>
                <Box>
              <Typography variant="h6" gutterBottom>Логика переходов</Typography>
              
              {/* Page-based logic */}
              {pages.length > 0 ? (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Настройте условные переходы между страницами на основе ответов респондента. При выборе определенного ответа респондент перейдет на указанную страницу или опрос завершится.
                  </Alert>
                  
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    onClick={handleAddPageLogic} 
                    sx={{ mb: 2 }}
                    disabled={questions.length === 0 || pages.length < 1}
                  >
                    Добавить правило
                  </Button>

                  {pageLogic.length === 0 && (
                    <Alert severity="info">
                      Нет правил логики. Без правил респонденты будут переходить последовательно через все страницы.
                    </Alert>
                  )}

                  {pageLogic.map((rule) => {
                    const question = getQuestionById(rule.questionId);
                    const questionPage = getPageForQuestion(rule.questionId);
                    const options = getAnswerOptionsForQuestion(question);
                    const disabled = options.length === 0;

                    return (
                      <Card key={rule.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2} alignItems="center">
                            {/* Question selector */}
                            <Grid item xs={12} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Вопрос</InputLabel>
                                <Select
                                  value={rule.questionId || ''}
                                  label="Вопрос"
                                  onChange={(e) => handleUpdatePageLogic(rule.id, { questionId: e.target.value })}
                                >
                                  {questions.map(q => (
                                    <MenuItem key={q.id} value={q.id}>
                                      {q.text.length > 30 ? q.text.slice(0, 30) + '...' : q.text}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Condition */}
                            <Grid item xs={12} sm={4}>
                              {question && isMultiSelectQuestion(question) ? (
                                <FormControl fullWidth size="small">
                                  <InputLabel>Если выбран любой из</InputLabel>
                                  <Select
                                    multiple
                                    value={rule.condition?.answers || []}
                                    label="Если выбран любой из"
                                    onChange={(e) => handleUpdatePageLogicCondition(rule.id, { answers: e.target.value })}
                                    disabled={disabled}
                                    renderValue={(selected) => selected.join(', ')}
                                  >
                                    {options.map(opt => (
                                      <MenuItem key={opt.value} value={opt.value}>
                                        <Checkbox checked={(rule.condition?.answers || []).includes(opt.value)} />
                                        <ListItemText primary={opt.label} />
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <FormControl fullWidth size="small">
                                  <InputLabel>Если ответ</InputLabel>
                                  <Select
                                    value={rule.condition?.answer || ''}
                                    label="Если ответ"
                                    onChange={(e) => handleUpdatePageLogicCondition(rule.id, { answer: e.target.value })}
                                    disabled={disabled}
                                  >
                                    {options.map(opt => (
                                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            </Grid>

                            {/* Action */}
                            <Grid item xs={12} sm={4}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Перейти на страницу</InputLabel>
                                <Select
                                  value={rule.targetPageId || ''}
                                  label="Перейти на страницу"
                                  onChange={(e) => handleUpdatePageLogic(rule.id, { targetPageId: e.target.value })}
                                >
                                  {pages.map(p => (
                                    <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                                  ))}
                                  <MenuItem value="END">Завершить опрос</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Delete button */}
                            <Grid item xs={12} sm={1}>
                              <IconButton color="error" onClick={() => handleDeletePageLogic(rule.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Paper>
              ) : (
                <Alert severity="warning">
                  Сначала добавьте страницы и вопросы для настройки логики переходов.
                </Alert>
              )}
            </Box>
          </Grid>

          {/* Right: Preview */}
          <Grid item xs={12} md={4}>
            <LivePreview 
              survey={basicInfo}
              questions={questions}
              pages={pages}
              pageLogic={pageLogic}
            />
          </Grid>
        </Grid>
          );
        case 3: // Настройки (только maxResponses)
          return (
            <Box>
              <Typography variant="h6" gutterBottom>Настройки публичного опроса</Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Для опросов по прямой ссылке не требуются квоты и таргетинг. Укажите только максимальное количество ответов.
              </Alert>
              
              <TextField
                fullWidth
                type="number"
                label="Максимальное количество ответов"
                value={maxResponses}
                onChange={(e) => setMaxResponses(parseInt(e.target.value) || 100)}
                inputProps={{ min: 1 }}
                helperText="Опрос будет автоматически закрыт после получения указанного количества ответов"
                sx={{ mb: 2 }}
              />
            </Box>
          );
        default:
          return null;
      }
    }

    // Regular surveys
    switch (activeStep) {
      case 0: // Базовая информация
        return renderBasicInfo();
      case 1: // Вопросы (перенесена настройка пагинации сюда)
        return renderQuestions();
      case 2: // Логика и ветвления
        return (
          <Grid container spacing={2}>
            {/* Left: Logic Editor */}
            <Grid item xs={12} md={8}>
              <Box>
            <Typography variant="h6" gutterBottom>Логика переходов</Typography>
            
            {/* Page-based logic */}
            {pages.length > 0 ? (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Настройте условные переходы между страницами на основе ответов респондента. При выборе определенного ответа респондент перейдет на указанную страницу или опрос завершится.
                </Alert>
                
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />} 
                  onClick={handleAddPageLogic} 
                  sx={{ mb: 2 }}
                  disabled={questions.length === 0 || pages.length < 1}
                >
                  Добавить правило
                </Button>

                {pageLogic.length === 0 && (
                  <Alert severity="info">
                    Нет правил логики. Без правил респонденты будут переходить последовательно через все страницы.
                  </Alert>
                )}

                {pageLogic.map((rule) => {
                  const question = getQuestionById(rule.questionId);
                  const questionPage = getPageForQuestion(rule.questionId);
                  const options = getAnswerOptionsForQuestion(question);
                  const disabled = options.length === 0;

                  return (
                    <Card key={rule.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          {/* Question selector */}
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Вопрос</InputLabel>
                              <Select
                                value={rule.questionId}
                                label="Вопрос"
                                onChange={(e) => {
                                  const newQId = e.target.value;
                                  const defaultTarget = pages[1]?.id || pages[0]?.id || null;
                                  handleUpdatePageLogic(rule.id, {
                                    questionId: newQId,
                                    condition: { answers: [], answer: '' },
                                    action: 'goToPage',
                                    targetPageId: defaultTarget
                                  });
                                }}
                              >
                                {questions.map((q, i) => {
                                  const qPage = getPageForQuestion(q.id);
                                  const pageNumber = qPage ? pages.findIndex(p => p.id === qPage.id) + 1 : null;
                                  return (
                                    <MenuItem key={q.id} value={q.id}>
                                      {i + 1}. {q.text.substring(0, 25)}... 
                                      {pageNumber && ` (Стр. ${pageNumber})`}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Answer selector */}
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small" disabled={disabled}>
                              <InputLabel>Если ответ</InputLabel>
                              <Select
                                multiple={isMultiSelectQuestion(question)}
                                value={Array.isArray(rule.condition?.answers) && rule.condition.answers.length > 0 
                                  ? rule.condition.answers 
                                  : (rule.condition?.answer ? [rule.condition.answer] : [])}
                                label="Если ответ"
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const arr = Array.isArray(val) ? val : [val];
                                  handleUpdatePageLogicCondition(rule.id, { 
                                    answers: arr, 
                                    answer: arr[0] || '' 
                                  });
                                }}
                                renderValue={(selected) => (selected || []).join(', ')}
                              >
                                {options.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Action selector - goToPage or end */}
                          <Grid item xs={12} sm={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Действие</InputLabel>
                              <Select
                                value={rule.action || 'goToPage'}
                                label="Действие"
                                onChange={(e) => {
                                  const newAction = e.target.value;
                                  handleUpdatePageLogic(rule.id, { 
                                    action: newAction,
                                    targetPageId: (newAction === 'end' || newAction === 'endWithoutPayment') ? null : (rule.targetPageId || pages[0]?.id)
                                  });
                                }}
                              >
                                <MenuItem value="goToPage">Перейти к странице</MenuItem>
                                <MenuItem value="end">Завершить опрос</MenuItem>
                                <MenuItem value="endWithoutPayment">Завершить без оплаты</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Target page selector (only if action is goToPage) */}
                          <Grid item xs={12} sm={3}>
                            {rule.action !== 'end' && rule.action !== 'endWithoutPayment' && (
                              <FormControl fullWidth size="small">
                                <InputLabel>Целевая страница</InputLabel>
                                <Select
                                  value={rule.targetPageId || ''}
                                  label="Целевая страница"
                                  onChange={(e) => handleUpdatePageLogic(rule.id, { targetPageId: e.target.value })}
                                >
                                  {pages.map((page, idx) => (
                                    <MenuItem key={page.id} value={page.id}>
                                      Страница {idx + 1} ({page.questionIds.length} вопр.)
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {rule.action === 'end' && (
                              <TextField
                                fullWidth
                                size="small"
                                value="—"
                                disabled
                                label="Целевая страница"
                              />
                            )}
                          </Grid>

                          {/* Delete button */}
                          <Grid item xs={12} sm={1}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeletePageLogic(rule.id)} 
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>

                        {/* Disqualification reason input */}
                        {rule.action === 'endWithoutPayment' && (
                          <TextField
                            fullWidth
                            size="small"
                            label="Причина завершения без оплаты"
                            placeholder="Введите текст, который увидит респондент при дисквалификации"
                            value={rule.disqualificationReason || ''}
                            onChange={(e) => handleUpdatePageLogic(rule.id, { disqualificationReason: e.target.value })}
                            multiline
                            rows={2}
                            sx={{ mt: 2 }}
                            helperText="Оставьте пустым для отображения стандартного сообщения"
                          />
                        )}

                        {/* Warning for questions without options */}
                        {options.length === 0 && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            Логика по ответам доступна только для вопросов с вариантами: одиночный, множественный, выпадающий список, варианты с картинками, а также шкала Лайкерта.
                          </Alert>
                        )}

                        {/* Show which page the question is on */}
                        {questionPage && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            Вопрос находится на странице: <strong>Страница {pages.findIndex(p => p.id === questionPage.id) + 1}</strong>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Paper>
            ) : (
              <Alert severity="warning">
                Создайте страницы на шаге "Вопросы" для настройки логики переходов.
              </Alert>
            )}
              </Box>
            </Grid>

            {/* Right: Live Preview */}
            <Grid item xs={12} md={4}>
              <Box sx={stickyPreviewSx}>
                <LivePreview
                  survey={basicInfo}
                  questions={questions}
                  pages={pages}
                  pageLogic={pageLogic}
                  currentPageId={currentPageId}
                />
              </Box>
            </Grid>
          </Grid>
        );

      case 3: // Квоты (пагинация перенесена в шаг 2)
        return (
          <Box>
            {isAdmin && (
              <>
                <Typography variant="h6" gutterBottom>Награды</Typography>
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Деньги (BYN)"
                        type="number"
                        value={reward.moneyAmount || 0}
                        onChange={(e) => setReward({ ...reward, moneyAmount: parseFloat(e.target.value) || 0 })}
                        InputProps={{ endAdornment: 'BYN' }}
                        inputProps={{ step: 0.1, min: 0 }}
                        helperText="За прохождение опроса (с точностью до 0.1)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Подарочные баллы"
                        type="number"
                        value={reward.pointsAmount || 0}
                        onChange={(e) => setReward({ ...reward, pointsAmount: parseFloat(e.target.value) || 0 })}
                        InputProps={{ endAdornment: 'баллов' }}
                        inputProps={{ step: 0.1, min: 0 }}
                        helperText="За прохождение опроса (с точностью до 0.1)"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}

            <Typography variant="h6" gutterBottom>Динамический конструктор квот</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Формируйте квоты на основе ответов профильных опросов. В каждой квоте добавьте одно или несколько условий (вопрос + допустимые ответы). Респондент должен удовлетворять всем условиям квоты.
            </Alert>
            <Alert severity="success" sx={{ mb: 2 }}>
              * Если Вы хотите опубликовать опрос для всех пользователей, используйте условие "Ваш пол" и прокликайте оба ответа, и "Мужчина" и "Женщина". И выставьте количество ответов. Это условие подойдёт для всех пользователей платформы.
            </Alert>
            {loadingProfileQuestions && <Alert severity="info" sx={{ mb:2 }}>Загрузка профильных вопросов...</Alert>}
            {!loadingProfileQuestions && profileQuestions.length === 0 && (
              <Alert severity="warning" sx={{ mb:2 }}>Нет активных профильных вопросов с вариантами ответов. Создайте профильные опросы сначала.</Alert>
            )}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddQuota} sx={{ mb: 2 }} disabled={profileQuestions.length === 0}>
              Добавить квоту
            </Button>

            {quotas.map((quota) => (
              <Card key={quota.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Описание квоты"
                        value={quota.description || ''}
                        onChange={(e) => handleUpdateQuota(quota.id, 'description', e.target.value)}
                        placeholder="Например: Женщины купившие X товар"
                      />
                    </Grid>

                    {/* Dynamic conditions list */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>Условия квоты</Typography>
                      {(quota.criteria.dynamic || []).length === 0 && (
                        <Alert severity="warning" sx={{ mb:1 }}>Нет условий. Добавьте хотя бы одно условие.</Alert>
                      )}
                      {(quota.criteria.dynamic || []).map(cond => {
                        const question = profileQuestions.find(q => q.questionId === cond.questionId);
                        const availableAnswers = question ? question.options : [];
                        return (
                          <Paper key={cond.id} sx={{ p:2, mb:1, bgcolor:'#fafafa' }} variant="outlined">
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} md={5}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Вопрос профиля</InputLabel>
                                  <Select
                                    value={cond.questionId || ''}
                                    label="Вопрос профиля"
                                    onChange={(e) => handleUpdateDynamicCondition(quota.id, cond.id, { questionId: e.target.value, answers: [] })}
                                  >
                                    {profileQuestions.map(pq => (
                                      <MenuItem key={pq.questionId} value={pq.questionId}>
                                        {pq.surveyTitle}: {pq.questionText.substring(0,60)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={5}>
                                <FormControl fullWidth size="small" disabled={!cond.questionId}>
                                  <InputLabel>Допустимые ответы</InputLabel>
                                  <Select
                                    multiple
                                    value={cond.answers || []}
                                    label="Допустимые ответы"
                                    onChange={(e) => handleUpdateDynamicCondition(quota.id, cond.id, { answers: e.target.value })}
                                    renderValue={(selected) => (selected || []).join(', ')}
                                  >
                                    {availableAnswers.map(opt => (
                                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={2}>
                                <IconButton color="error" size="small" onClick={() => handleDeleteDynamicCondition(quota.id, cond.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Paper>
                        );
                      })}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        variant="outlined"
                        disabled={profileQuestions.length === 0}
                        onClick={() => handleAddDynamicCondition(quota.id)}
                      >
                        Добавить условие
                      </Button>
                    </Grid>

                    {/* Quota limit */}
                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Лимит"
                        type="number"
                        value={quota.limit}
                        onChange={(e) => handleUpdateQuota(quota.id, 'limit', parseInt(e.target.value) || 1)}
                        helperText="Макс. ответов"
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Заполнено"
                        type="number"
                        value={quota.filled || 0}
                        disabled
                        helperText="Текущее"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteQuota(quota.id)}>
                    Удалить квоту
                  </Button>
                </CardActions>
              </Card>
            ))}

            {quotas.length === 0 && (
              <Alert severity="error">Добавьте хотя бы одну квоту</Alert>
            )}

            {quotas.length > 0 && (
              <Alert severity="success" sx={{ mt: 3 }}>
                <Typography variant="subtitle2">Сводка по квотам</Typography>
                <Typography variant="body2" color="text.secondary">
                  Всего респондентов: {quotas.reduce((sum, q) => sum + q.limit, 0)}
                </Typography>
                {isAdminEditingExisting() && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      type="number"
                      size="small"
                      label="Общий лимит ответов (админ)"
                      value={maxResponses}
                      onChange={(e) => setMaxResponses(parseInt(e.target.value, 10) || 0)}
                      inputProps={{ min: 1 }}
                      helperText={`Сумма квот — ${quotasLimitSum()}, это подсказка. Сохраняется значение из этого поля. Собрано: ${loadedSurvey?.responseCount || 0}`}
                    />
                  </Box>
                )}
                {isAdmin && Number(reward.moneyAmount || 0) > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Итоговая стоимость</Typography>
                    <Typography variant="h6">{(Number(reward.moneyAmount || 0) * quotas.reduce((sum, q) => sum + q.limit, 0)).toFixed(2)} BYN</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quotas.reduce((sum, q) => sum + q.limit, 0)} респондентов × {Number(reward.moneyAmount || 0)} BYN
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div>Загрузка данных опроса...</div>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          {surveyId ? 'Редактирование опроса' : 'Создание опроса'}
        </Typography>

        <Stepper
          activeStep={activeStep}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ mb: 4 }}
        >
          {getBuilderSteps().map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && !questionDialogOpen && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {renderStepContent()}

        <Box sx={stepActionsSx}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Назад
          </Button>
          <Box sx={stepActionsGroupSx}>
            {/* У администратора две кнопки: сохранить на модерации и опубликовать, у заказчика — сохранить/отправить на модерацию */}
            {activeStep === getBuilderSteps().length - 1 ? (
              isAdmin ? (
                <>
                  <Button variant="outlined" color="primary" onClick={() => handleSubmit(false)} disabled={saving}>
                    Сохранить на модерации и выйти
                  </Button>
                  <Button variant="contained" color="success" onClick={() => handleSubmit(true)} disabled={saving}>
                    Опубликовать
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outlined" color="primary" onClick={() => handleSubmit(false)} disabled={saving}>
                    Сохранить черновик
                  </Button>
                  <Button variant="contained" color="success" onClick={() => handleSubmit(true)} disabled={saving}>
                    Отправить на модерацию
                  </Button>
                </>
              )
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Далее
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={duplicateNotice}
        autoHideDuration={5000}
        onClose={() => setDuplicateNotice(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDuplicateNotice(false)} severity="success" sx={{ width: '100%' }}>
          Копия опроса создана как черновик. Изменения не затронут исходный опрос.
        </Alert>
      </Snackbar>

      {/* Question Editor Dialog */}
      <Dialog
        open={questionDialogOpen}
        onClose={() => setQuestionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingQuestion && questions.find(q => q.id === editingQuestion.id) ? 'Редактировать вопрос' : 'Новый вопрос'}
        </DialogTitle>
        <DialogContent>
          {/* Show validation/error messages inside the dialog so they are visible when dialog is open */}
          {questionDialogOpen && error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {editingQuestion && (
            <Box>
              <TextField
                fullWidth
                label="Текст вопроса"
                value={editingQuestion.text}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
                margin="normal"
                multiline
                rows={2}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Тип вопроса</InputLabel>
                <Select
                  value={editingQuestion.type}
                  label="Тип вопроса"
                  onChange={(e) => {
                    const type = e.target.value;
                    const keepLimits = type === 'multiple' || type === 'multiple-image';
                    setEditingQuestion({
                      ...editingQuestion,
                      type,
                      minSelections: keepLimits ? (editingQuestion.minSelections ?? null) : null,
                      maxSelections: keepLimits ? (editingQuestion.maxSelections ?? null) : null,
                    });
                  }}
                >
                  {questionTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(editingQuestion.type === 'single' || editingQuestion.type === 'multiple' || editingQuestion.type === 'dropdown') && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Варианты ответов</Typography>
                  {editingQuestion.options.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...editingQuestion.options];
                          newOptions[index] = e.target.value;
                          setEditingQuestion({ ...editingQuestion, options: newOptions });
                        }}
                        placeholder={`Вариант ${index + 1}`}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newOptions = editingQuestion.options.filter((_, i) => i !== index);
                          setEditingQuestion({ ...editingQuestion, options: newOptions });
                        }}
                        disabled={editingQuestion.options.length <= 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setEditingQuestion({ ...editingQuestion, options: [...editingQuestion.options, ''] })}
                  >
                    Добавить вариант
                  </Button>
                </Box>
              )}

              {editingQuestion.type === 'ranking' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Варианты для ранжирования</Typography>
                  {editingQuestion.options.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...editingQuestion.options];
                          newOptions[index] = e.target.value;
                          setEditingQuestion({ ...editingQuestion, options: newOptions });
                        }}
                        placeholder={`Вариант ${index + 1}`}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newOptions = editingQuestion.options.filter((_, i) => i !== index);
                          setEditingQuestion({ ...editingQuestion, options: newOptions });
                        }}
                        disabled={editingQuestion.options.length <= 2}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setEditingQuestion({ ...editingQuestion, options: [...editingQuestion.options, ''] })}
                  >
                    Добавить вариант
                  </Button>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Респонденты должны упорядочить варианты по своему предпочтению
                  </Typography>
                </Box>
              )}

              {editingQuestion.type === 'matrix' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Матрица/таблица оценок</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">Строки (утверждения):</Typography>
                      {(editingQuestion.matrixRows || ['']).map((row, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={row}
                            onChange={e => {
                              const newRows = [...(editingQuestion.matrixRows || [])];
                              newRows[idx] = e.target.value;
                              setEditingQuestion({ ...editingQuestion, matrixRows: newRows });
                            }}
                            placeholder={`Строка ${idx + 1}`}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newRows = (editingQuestion.matrixRows || []).filter((_, i) => i !== idx);
                              setEditingQuestion({ ...editingQuestion, matrixRows: newRows });
                            }}
                            disabled={(editingQuestion.matrixRows || []).length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setEditingQuestion({ ...editingQuestion, matrixRows: [...(editingQuestion.matrixRows || []), ''] })}
                      >
                        Добавить строку
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">Столбцы (оценки):</Typography>
                      {(editingQuestion.matrixCols || ['']).map((col, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={col}
                            onChange={e => {
                              const newCols = [...(editingQuestion.matrixCols || [])];
                              newCols[idx] = e.target.value;
                              setEditingQuestion({ ...editingQuestion, matrixCols: newCols });
                            }}
                            placeholder={`Столбец ${idx + 1}`}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newCols = (editingQuestion.matrixCols || []).filter((_, i) => i !== idx);
                              setEditingQuestion({ ...editingQuestion, matrixCols: newCols });
                            }}
                            disabled={(editingQuestion.matrixCols || []).length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setEditingQuestion({ ...editingQuestion, matrixCols: [...(editingQuestion.matrixCols || []), ''] })}
                      >
                        Добавить столбец
                      </Button>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Респонденты выбирают оценку для каждого утверждения
                  </Typography>
                </Box>
              )}

              {editingQuestion.type === 'likert' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Варианты согласия/несогласия</Typography>
                  {(editingQuestion.likertOptions || [
                    'Полностью не согласен',
                    'Скорее не согласен',
                    'Нейтрально',
                    'Скорее согласен',
                    'Полностью согласен'
                  ]).map((opt, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={opt}
                        onChange={e => {
                          const newOpts = [...(editingQuestion.likertOptions || [])];
                          newOpts[idx] = e.target.value;
                          setEditingQuestion({ ...editingQuestion, likertOptions: newOpts });
                        }}
                        placeholder={`Вариант ${idx + 1}`}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newOpts = (editingQuestion.likertOptions || []).filter((_, i) => i !== idx);
                          setEditingQuestion({ ...editingQuestion, likertOptions: newOpts });
                        }}
                        disabled={(editingQuestion.likertOptions || []).length <= 2}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setEditingQuestion({ ...editingQuestion, likertOptions: [...(editingQuestion.likertOptions || []), ''] })}
                  >
                    Добавить вариант
                  </Button>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Классическая шкала согласия/несогласия по утверждению
                  </Typography>
                </Box>
              )}

              {editingQuestion.type === 'nps' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Net Promoter Score (NPS)</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Оцените по шкале от 0 до 10, насколько вы готовы порекомендовать продукт/услугу
                  </Typography>
                </Box>
              )}

              {editingQuestion.type === 'scale' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Параметры шкалы</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Минимум (от)"
                        type="number"
                        value={editingQuestion.scaleMin ?? 0}
                        onChange={e => setEditingQuestion({ ...editingQuestion, scaleMin: parseInt(e.target.value) })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Максимум (до)"
                        type="number"
                        value={editingQuestion.scaleMax ?? 10}
                        onChange={e => setEditingQuestion({ ...editingQuestion, scaleMax: parseInt(e.target.value) })}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Укажите диапазон значений шкалы (например, от 1 до 5, от 0 до 10)
                  </Typography>
                </Box>
              )}

              {(editingQuestion.type === 'single-image' || editingQuestion.type === 'multiple-image') && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Варианты ответов с картинками</Typography>
                  {(editingQuestion.imageOptions || []).map((option, index) => (
                    <Card key={index} sx={{ mb: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={option.text || ''}
                            onChange={(e) => {
                              const newOptions = [...(editingQuestion.imageOptions || [])];
                              newOptions[index] = { ...newOptions[index], text: e.target.value };
                              setEditingQuestion({ ...editingQuestion, imageOptions: newOptions });
                            }}
                            placeholder={`Название варианта ${index + 1}`}
                            sx={{ mb: 1 }}
                          />
                          <Button
                            component="label"
                            size="small"
                            variant="outlined"
                          >
                            {option.imageUrl ? 'Изменить изображение' : 'Загрузить изображение'}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, index)}
                            />
                          </Button>
                          {option.imageUrl && (
                            <Box sx={{ mt: 1 }}>
                              <img
                                src={option.imageUrl.startsWith('/') ? `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${option.imageUrl}` : option.imageUrl}
                                alt={option.text || `Вариант ${index + 1}`}
                                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                            </Box>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newOptions = (editingQuestion.imageOptions || []).filter((_, i) => i !== index);
                            setEditingQuestion({ ...editingQuestion, imageOptions: newOptions });
                          }}
                          disabled={(editingQuestion.imageOptions || []).length <= 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newOptions = [...(editingQuestion.imageOptions || []), { text: '', imageUrl: '' }];
                      setEditingQuestion({ ...editingQuestion, imageOptions: newOptions });
                    }}
                  >
                    Добавить вариант с картинкой
                  </Button>
                </Box>
              )}

              {/* Общие настройки для всех типов вопросов */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                {(editingQuestion.type === 'multiple' || editingQuestion.type === 'multiple-image') && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Число выбранных вариантов
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingQuestion.minSelections != null}
                          onChange={(e) => setEditingQuestion({
                            ...editingQuestion,
                            minSelections: e.target.checked
                              ? (editingQuestion.minSelections ?? '')
                              : null,
                          })}
                        />
                      }
                      label="Минимум (не меньше)"
                      sx={{ display: 'block' }}
                    />
                    {editingQuestion.minSelections != null && (
                      <TextField
                        size="small"
                        type="number"
                        label="Не меньше"
                        value={editingQuestion.minSelections}
                        onChange={(e) => setEditingQuestion({
                          ...editingQuestion,
                          minSelections: e.target.value,
                        })}
                        inputProps={{ min: 1, step: 1 }}
                        helperText="Респондент не сможет продолжить, пока не выберет столько вариантов"
                        sx={{ mb: 1, maxWidth: 360, display: 'block' }}
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingQuestion.maxSelections != null}
                          onChange={(e) => setEditingQuestion({
                            ...editingQuestion,
                            maxSelections: e.target.checked
                              ? (editingQuestion.maxSelections ?? '')
                              : null,
                          })}
                        />
                      }
                      label="Максимум (не больше)"
                      sx={{ display: 'block' }}
                    />
                    {editingQuestion.maxSelections != null && (
                      <TextField
                        size="small"
                        type="number"
                        label="Не больше"
                        value={editingQuestion.maxSelections}
                        onChange={(e) => setEditingQuestion({
                          ...editingQuestion,
                          maxSelections: e.target.value,
                        })}
                        inputProps={{ min: 1, max: countBuilderOptions(editingQuestion) || undefined, step: 1 }}
                        helperText="Респондент сможет выбрать не больше этого числа. Свой вариант тоже считается"
                        sx={{ mb: 1, maxWidth: 360, display: 'block' }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Оставьте выключенным, чтобы не ограничивать эту границу. Сейчас вариантов: {countBuilderOptions(editingQuestion)}.
                      Если вопрос обязательный и минимум не задан, нужно выбрать хотя бы 1.
                    </Typography>
                  </Box>
                )}
                {(editingQuestion.type === 'single' || editingQuestion.type === 'multiple' || 
                  editingQuestion.type === 'dropdown' || editingQuestion.type === 'single-image' || 
                  editingQuestion.type === 'multiple-image') && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editingQuestion.allowCustomOption || false}
                        onChange={(e) => setEditingQuestion({ ...editingQuestion, allowCustomOption: e.target.checked })}
                      />
                    }
                    label="Разрешить респонденту указать свой вариант"
                    sx={{ display: 'block', mb: 1 }}
                  />
                )}
                {(editingQuestion.type === 'single' || editingQuestion.type === 'multiple' || 
                  editingQuestion.type === 'dropdown' || editingQuestion.type === 'single-image' || 
                  editingQuestion.type === 'multiple-image' || editingQuestion.type === 'ranking') && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editingQuestion.randomizeOptions || false}
                        onChange={(e) => setEditingQuestion({ ...editingQuestion, randomizeOptions: e.target.checked })}
                      />
                    }
                    label="Случайный порядок ответов"
                    sx={{ display: 'block', mb: 1 }}
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingQuestion.required}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, required: e.target.checked })}
                    />
                  }
                  label="Обязательный вопрос"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 1 },
          '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' }, m: 0 },
        }}>
          <Button onClick={() => setQuestionDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSaveQuestion}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
